import Hashes from "jshashes";
import React, { useEffect, useState, memo, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Card,
  Typography,
  CardMedia,
  Tooltip,
  useTheme,
  Box,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

import {
  setBookmarkedCollectionIds,
  setCollectionId,
  setCollectionSourceId,
  setEntityName,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSourceSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setOthersNeeded,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSourceUrl,
  setSummaryInfoData,
} from "context/features/dataSlice";
import defaultBackground from "assets/images/image-placeholder.png";
import { ReactComponent as EbscoLogo } from "assets/icons/EBSCO_Information_Services_20xx_logo.svg";

import { ReactComponent as CollectionsLogo } from "assets/icons/bookmark-blue-icon.svg";
import { ReactComponent as CollectionsLogoHover } from "assets/icons/bookmark-hover-icon.svg";
import { HiOutlineLockClosed } from "react-icons/hi2";
import { ImInfo } from "react-icons/im";
import NewsguardScore from "components/base/NewsguardScore";
import AllSidesRating from "components/base/all-sides/AllSidesRating";
import { logEvent } from "services/amplitude.service.js";
import CharityNavigatorScore from "components/base/CharityNavigatorScore";
// import { useIsTab } from "hooks/is-tab.hook";
import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import SearchResultLink from "features/SourceSearch/components/SearchResultLink";
import FileTypeIcon from "components/base/FileTypeIcon";
import { devViewPdf } from "config";
import { showNewsguard } from "utils/show-ratings";
// import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import OpenAccessChip from "features/CategoryNews/component/open-access-chip";
import SmileyIcon from "./smiley-icon";
import { useSwiper, useSwiperSlide } from "swiper/react";
import AccessibleIconButton from "components/base/AccessibleIconButton";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";

const NewsCardComponent = ({ data, query, category, slideIndex }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  // const isTab = useIsTab();
  const dispatch = useDispatch();

  const hashId = useMemo(() => {
    return new Hashes.MD5().hex(data.url);
  }, [data]);

  const isDatabaseProvider = ["ebsco", "proquest"].includes(
    String(category).toLowerCase()
  );
  const isEbsco = String(category).toLowerCase() === "ebsco";
  const hasPublisherInfo = !isDatabaseProvider && data.hostName != null;

  const openSourceSidebar = (tour = false) => {
    if (isDatabaseProvider) return;
    if (!tour) {
      logEvent("click_card_header", {
        host: data.hostName,
        entity: data.entityName,
      });
    }
    dispatch(setOthersNeeded(true));
    dispatch(setIsSourceSidebarOpen(true));
    dispatch(setSourceUrl(data.hostName));
    dispatch(setEntityName(data.entityName));
  };

  const DatabaseDetails = () => {
    const authors = Array.isArray(data.authors)
      ? data.authors.filter((author) => typeof author === "string" && author.trim())
      : [];
    const publication = [
      data.sourceTitle || data.entityName,
      data.datePublishedDisplayText,
      data.volume && `Vol. ${data.volume}`,
      data.issue && `Issue ${data.issue}`,
    ].filter(Boolean);
    const database = data.dbLabel || data.dbId;

    const compactTextSx = { m: 0, overflowWrap: "anywhere" };
    const authorPreview = authors.length > 1
      ? `${authors[0]} (+${authors.length - 1} more)`
      : authors.join("; ");
    const sourceDetails = data.docType;

    return (
      <Stack gap={0.25} sx={{ minWidth: 0 }}>
        {authors.length > 0 && (
          <Typography component="div" variant="body2" title={authors.join("; ")} sx={compactTextSx}>
            {authorPreview}
          </Typography>
        )}
        {publication.length > 0 && (
          <Typography component="div" variant="body2" color="text.secondary" title={publication.join(" · ")} sx={compactTextSx}>
            {publication.join(" · ")}
          </Typography>
        )}
        {(sourceDetails || data.hasPdf === true) && (
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
            {sourceDetails && (
              <Typography component="div" variant="body2" color="text.secondary" title={[sourceDetails, database].filter(Boolean).join(" · ")} sx={{ ...compactTextSx, minWidth: 0 }}>
                {sourceDetails}
              </Typography>
            )}
            {data.hasPdf === true && (
              <Tooltip title="PDF available">
                <Box component="span" role="img" aria-label="PDF available" sx={{ display: "inline-flex", flexShrink: 0, color: "text.secondary" }}>
                  <PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
    );
  };

  const CardHeader = () => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
        onClick={isDatabaseProvider ? undefined : openSourceSidebar}
      sx={{
        borderRadius: 1,
        px: "1rem",
        py: 0.4,
        cursor: hasPublisherInfo ? "pointer" : "default",
        ...(hasPublisherInfo && {
          "&:hover": { backgroundColor: "secondary.main" },
        }),
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.75} minWidth={0}>
        <Tooltip
          enterDelay={750}
          followCursor
          title={data.entityName}
          placement="bottom-start"
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
              width: "20rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "grey.600",
            }}
          >
            {data.entityName}
          </Typography>
        </Tooltip>
      </Stack>
      <Stack direction="row" alignItems="center" gap={2}>
        {data.hasOpenAccess &&
          category === "Academia & Scholarly Resources" && <OpenAccessChip />}
        {data.mediaBiasRating && (
          <AllSidesRating
            size="small"
            single
            activeCategory={data.mediaBiasRating}
            className={category === "Global News" ? "tour-all-sides-icon" : ""}
            hash={hashId}
          />
        )}
        {showNewsguard(data.score) && (
          <Box
            className={category === "Global News" ? "tour-newsguard-icon" : ""}
            data-article-id={hashId}
          >
            <NewsguardScore score={Number(data.score)} size="small" />
          </Box>
        )}
        {data.charityNavigatorScore &&
          category !== "Academia & Scholarly Resources" &&
          !data.score &&
          !data.mediaBiasRating && (
            <Box className="tour-charity-icon" data-article-id={hashId}>
              <CharityNavigatorScore
                score={data.charityNavigatorScore}
                size="small"
              />
            </Box>
          )}
        {!isDatabaseProvider && !hasPublisherInfo && (
          <Tooltip title="No source information available">
            <Box
              component="span"
              sx={{ display: "inline-flex", color: "grey.500" }}
            >
              <ImInfo size={15} />
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );

  const openSidebar = () => {
    const collectionIds = (data.collectionMemberships || [])
      .map((membership) => membership.collectionID_lng)
      .filter(Boolean);

    dispatch(setSelectedBookmarkOption("notes"));
    dispatch(setLevel(""));
    dispatch(setCollectionSourceId(data.collectionSourceId));
    dispatch(
      setSummaryInfoData({
        publisher: data.entityName,
        title: data.name,
        published_date: data.datePublishedDisplayText,
        url: data.url,
        utc_date_published: data.datePublished,
        dbId: String(category).toLowerCase() === "ebsco" ? data.dbId ?? data.DbId : undefined,
        an: String(category).toLowerCase() === "ebsco" ? data.an ?? data.An : undefined,
      }),
    );
    dispatch(setCollectionId(data.collectionId ?? collectionIds[0]));
    dispatch(
      setBookmarkedCollectionIds(
        collectionIds.length > 0
          ? collectionIds
          : data.collectionId
          ? [data.collectionId]
          : []
      )
    );
    dispatch(setIsCitationAvailable(data.isCitationAvailable));
    dispatch(setIsSummaryAvailable(data.isSummaryAvailable));
    dispatch(setReadingLevelAvailable(data.isReadingLevelAvailable));
    dispatch(setIsSidebarOpen(true));
  };

  const newUrl = data.url.includes("foreignpolicy.com")
    ? data.url + "?auth=sooth"
    : data.url;

  const BookmarkOptions = () => (
    <Stack
      onClick={() => {
        openSidebar();
        logEvent("open_bookmark_popup", {
          url: newUrl,
          query,
          type: "generic",
          entity: data.entityName,
          category,
          mediabiasrating: data.mediaBiasRating || "NA",
          score: showNewsguard(data.score) ? data.score : "NA",
        });
      }}
      className="card-options"
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      gap={1}
      mt={isDatabaseProvider ? 0 : 1}
      sx={{
        cursor: "pointer",
        pl: 1,
        pr: 1,
        borderRadius: 1,
        transition: "all 0.1s ease",
        ...(isEbsco && {
          position: "absolute",
          right: 8,
          bottom: 8,
        }),
        "&:hover": {
          backgroundColor: theme.palette.secondary.main,
          "& .collection-hover": { opacity: "1 !important" },
        },
      }}
    >
      {data.collectionSourceId ? (
        <CollectionsLogo
          onClick={openSidebar}
          alt="Collections"
          style={{ width: 18, height: 18, fill: "blue", color: "blue" }}
        />
      ) : (
        <CollectionsLogoHover
          className="collection-hover"
          onClick={openSidebar}
          alt="Collections"
          style={{
            width: 17,
            height: 17,
            fill: "blue",
            color: "blue",
            opacity: 0,
            transition: "all 0.1s ease",
          }}
        />
      )}
      <AccessibleIconButton
        title="Open Bookmark"
        sx={{
          p: 0,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        <MoreHorizIcon />
      </AccessibleIconButton>
    </Stack>
  );

  const ImageSection = ({ isEbscoImage = false }) => {
    const imageUrl = data?.openGraphImage?.contentUrl;
    const [errored, setErrored] = useState(false);

    const onError = ({ currentTarget }) => {
      if (!errored) {
        setErrored(true);
        currentTarget.src = defaultBackground;
        currentTarget.alt = "Image not found";
      }
    };

    return (
      <Box
        sx={{
          flexGrow: 1,
          flexBasis: { xs: "30%", sm: "45%", md: "30%", lg: "35%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: isEbscoImage ? "flex-end" : "space-between",
          alignItems: "flex-end",
          height: "100%",
        }}
      >
        {isEbscoImage && imageUrl && !errored ? (
          <CardMedia
            component="img"
            sx={{
              borderRadius: "10px",
              height: { xs: "6em", md: "6em" },
              width: "100%",
              objectFit: "contain",
            }}
            image={imageUrl}
            alt={data.name || "EBSCO result"}
            onError={() => setErrored(true)}
          />
        ) : isEbscoImage ? (
          <Box sx={{ height: { xs: "6em", md: "6em" }, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", borderRadius: "10px" }}>
            <EbscoLogo aria-label="EBSCO Information Services" style={{ width: "100%", height: "100%" }} />
          </Box>
        ) : (
          <CardMedia
            component="img"
            sx={{
              aspectRatio: 1,
              borderRadius: "10px",
              minHeight: "80px",
              width: "100%",
              objectFit: "cover",
              maxHeight: { xs: "6em", md: "unset" },
            }}
            image={imageUrl || defaultBackground}
            alt={imageUrl ? data.name : "Image not found"}
            onError={onError}
          />
        )}
        <BookmarkOptions />
      </Box>
    );
  };

  const swiper = useSwiper();

  useEffect(() => {
    function triggerSlideTo({ detail }) {
      console.log("swiping", slideIndex, swiper);
      swiper.slideTo(slideIndex);
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent(detail.action, { detail: detail.index }),
        );
      }, 0);
    }
    function triggerOpenSourcePanel({ detail }) {
      openSourceSidebar(true);
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("tour:app:sourcePanel:onLoad", { detail }),
        );
      }, 0);
    }
    window.addEventListener(
      `tour:app:article:slideTo:${hashId}`,
      triggerSlideTo,
    );
    window.addEventListener(
      `tour:app:article:sourceOpen:${hashId}`,
      triggerOpenSourcePanel,
    );
    return () => {
      window.removeEventListener(
        `tour:app:article:slideTo:${hashId}`,
        triggerSlideTo,
      );
      window.removeEventListener(
        `tour:app:article:sourceOpen:${hashId}`,
        triggerOpenSourcePanel,
      );
    };
  }, [hashId]);

  return (
    <Card
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        boxShadow: "none",
        ...(isDatabaseProvider && { boxSizing: "border-box" }),
        borderRadius: 1,
        p: 1,
        transition: `all ${theme.transitions.duration.standard}ms ${theme.transitions.easing.sharp}`,
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Stack direction="column" gap={isDatabaseProvider ? 0.5 : 1} justifyContent={isDatabaseProvider ? "flex-start" : "space-between"} sx={isDatabaseProvider ? { height: "100%" } : undefined}>
        <Box sx={{ width: "100%" }}>
          {(!isDatabaseProvider || String(category).toLowerCase() === "ebsco") && <CardHeader />}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              p: "0 1rem",
              mt: isDatabaseProvider ? 0 : 1,
            }}
          >
            <Box
              sx={{
                maxHeight: isDatabaseProvider ? "none" : "5em",
                mb: isDatabaseProvider ? 0 : 2,
                minHeight: isDatabaseProvider ? 0 : "3em",
                display: "flex",
                alignItems: "flex-start",
                gap: 0.5,
              }}
            >
              <SearchResultLink
                url={data.url}
                trackingCat="click_resultset_title"
                trackingOptions={{
                  url: data.url,
                  query: query?.trim() ? query : undefined,
                  entity: data.entityName,
                  title: data.name,
                  category,
                  mediabiasrating: data.mediaBiasRating || "NA",
                  score: showNewsguard(data.score) ? data.score : "NA",
                  date_published: data.datePublished,
                }}
                variant="body4"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: isDatabaseProvider ? "visible" : "hidden",
                  textOverflow: "ellipsis",
                  display: isDatabaseProvider ? "block" : "-webkit-box",
                  WebkitLineClamp: 2,
                  overflowWrap: "anywhere",
                  WebkitBoxOrient: "vertical",
                  lineHeight: "1.5em",
                  cursor: "pointer",
                  "&:hover": { color: theme.palette.primary.light },
                }}
              >
                {isDatabaseProvider ? (
                  <Box component="span" sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: "1.5em",
                    pt: "2px",
                  }}>
                    {data.name}
                  </Box>
                ) : data.name}
              </SearchResultLink>
              {devViewPdf && (
                <FileTypeIcon
                  url={data.url}
                  sx={{ flexShrink: 0, mt: "2px" }}
                />
              )}
            </Box>
            {data.hasPayWall && data.isPartner ? (
              <SmileyIcon />
            ) : (
              data.hasPayWall && (
                <Tooltip
                  placement="bottom-start"
                  title="This source may limit your access without a subscription"
                >
                  <Box sx={{ cursor: "pointer" }}>
                    <HiOutlineLockClosed />
                  </Box>
                </Tooltip>
              )
            )}
          </Box>
        </Box>

        {isDatabaseProvider && !isEbsco && (
          <Box sx={{ px: 2 }}>
            <DatabaseDetails />
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "stretch", px: 1 }}>
          <Stack
            sx={{
              width: "100%",
              flexGrow: 1,
              flexBasis: { xs: "70%", sm: "50%", md: "50%", lg: "65%" },
              pr: 2,
              flexShrink: 0,
            }}
          >
            <Typography
              variant={isMobile ? "body4" : "body1"}
              sx={{
                color: isDatabaseProvider ? "text.secondary" : "grey.700",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: isDatabaseProvider ? 3 : { xs: 4, md: 4, lg: 5 },
                WebkitBoxOrient: "vertical",
                maxHeight: isDatabaseProvider ? "4.5em" : { xs: "6em", md: "6em", lg: "7.5em" },
                lineHeight: "1.5em",
                minHeight: isDatabaseProvider ? 0 : "4.5em",
                wordBreak: "break-word",
              }}
            >
              {!isDatabaseProvider && !isMobile && data.datePublishedDisplayText
                ? `${data.datePublishedDisplayText} - `
                : ""}
              {data.snippet}
            </Typography>
          </Stack>
          {!isDatabaseProvider ? (
            <ImageSection />
          ) : String(category).toLowerCase() === "ebsco" ? (
            <ImageSection isEbscoImage />
          ) : null}
        </Box>
        {isDatabaseProvider && !isEbsco && (
          <Box sx={{ mt: "auto" }}>
            <BookmarkOptions />
          </Box>
        )}
      </Stack>
    </Card>
  );
};

export const NewsCard = memo(NewsCardComponent);
